export type PresenceDetectorSource = 'native' | 'mediapipe';

export interface PresenceDetector {
  readonly source: PresenceDetectorSource;
  detect(video: HTMLVideoElement): Promise<boolean>;
  dispose(): Promise<void>;
}

interface NativeFaceDetector {
  detect(video: HTMLVideoElement): Promise<unknown[]>;
}

type NativeFaceDetectorConstructor = new (options?: { fastMode?: boolean; maxDetectedFaces?: number }) => NativeFaceDetector;

interface MediaPipeResults {
  detections: unknown[];
}

interface MediaPipeSolution {
  setOptions(options: { model: string; minDetectionConfidence: number }): void;
  onResults(listener: (results: MediaPipeResults) => void): void;
  initialize(): Promise<void>;
  send(input: { image: HTMLCanvasElement }): Promise<void>;
  close(): Promise<void>;
}

interface MediaPipeModule {
  FaceDetection: new (config: { locateFile: (path: string) => string }) => MediaPipeSolution;
}

interface BrowserPresenceDetectorOptions {
  preferFallback?: boolean;
  analysisWidthPx: number;
  analysisHeightPx: number;
  detectionTimeoutMs: number;
  nativeConstructor?: NativeFaceDetectorConstructor | null;
  loadMediaPipe?: () => Promise<MediaPipeModule>;
}

const MEDIA_PIPE_ASSET_ROOT = '/mediapipe/face_detection';

class NativePresenceDetector implements PresenceDetector {
  readonly source = 'native' as const;

  constructor(private readonly detector: NativeFaceDetector) {}

  async detect(video: HTMLVideoElement): Promise<boolean> {
    return (await this.detector.detect(video)).length > 0;
  }

  async dispose(): Promise<void> {}
}

class MediaPipePresenceDetector implements PresenceDetector {
  readonly source = 'mediapipe' as const;
  private readonly canvas = document.createElement('canvas');
  private readonly context: CanvasRenderingContext2D;
  private pending?: { resolve: (present: boolean) => void; reject: (error: unknown) => void; timer: number };

  constructor(
    private readonly detector: MediaPipeSolution,
    width: number,
    height: number,
    private readonly timeoutMs: number
  ) {
    this.canvas.width = width;
    this.canvas.height = height;
    const context = this.canvas.getContext('2d');
    if (!context) throw new Error('Presence analysis canvas is unavailable');
    this.context = context;
    this.detector.onResults((results) => this.resolvePending(results.detections.length > 0));
  }

  async detect(video: HTMLVideoElement): Promise<boolean> {
    if (this.pending) throw new Error('Presence detection is already running');
    this.context.drawImage(video, 0, 0, this.canvas.width, this.canvas.height);
    return new Promise<boolean>((resolve, reject) => {
      const timer = window.setTimeout(() => this.rejectPending(new Error('Presence detection timed out')), this.timeoutMs);
      this.pending = { resolve, reject, timer };
      try {
        void this.detector.send({ image: this.canvas }).catch((error) => this.rejectPending(error));
      } catch (error) {
        this.rejectPending(error);
      }
    });
  }

  async dispose(): Promise<void> {
    this.rejectPending(new Error('Presence detector disposed'));
    this.canvas.width = 0;
    this.canvas.height = 0;
    try { await this.detector.close(); } catch { /* Cleanup remains best-effort. */ }
  }

  private resolvePending(present: boolean): void {
    const pending = this.pending;
    if (!pending) return;
    this.pending = undefined;
    window.clearTimeout(pending.timer);
    pending.resolve(present);
  }

  private rejectPending(error: unknown): void {
    const pending = this.pending;
    if (!pending) return;
    this.pending = undefined;
    window.clearTimeout(pending.timer);
    pending.reject(error);
  }
}

export async function createBrowserPresenceDetector(options: BrowserPresenceDetectorOptions): Promise<PresenceDetector> {
  const NativeDetector = options.nativeConstructor === undefined
    ? (window as typeof window & { FaceDetector?: NativeFaceDetectorConstructor }).FaceDetector
    : options.nativeConstructor ?? undefined;

  if (!options.preferFallback && NativeDetector) {
    try {
      return new NativePresenceDetector(new NativeDetector({ fastMode: true, maxDetectedFaces: 1 }));
    } catch {
      // Fall through to the locally bundled detector.
    }
  }

  const loadMediaPipe = options.loadMediaPipe ?? (() => import('@mediapipe/face_detection') as Promise<MediaPipeModule>);
  const module = await loadMediaPipe();
  const solution = new module.FaceDetection({ locateFile: (path) => `${MEDIA_PIPE_ASSET_ROOT}/${path}` });
  try {
    solution.setOptions({ model: 'short', minDetectionConfidence: 0.55 });
    const detector = new MediaPipePresenceDetector(
      solution,
      options.analysisWidthPx,
      options.analysisHeightPx,
      options.detectionTimeoutMs
    );
    await solution.initialize();
    return detector;
  } catch (error) {
    try { await solution.close(); } catch { /* Initialization already failed. */ }
    throw error;
  }
}
